(ns renolit-roofing-service.view.installer-service-information.zone
  (:require [re-frame.core :as rf]
            [renolit-roofing-service.config :as config]
            [renolit-roofing-service.layout.components :refer [entity-section]]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.service.project :as srv.project]
            [renolit-roofing-service.util.google-maps :as google-maps]
            ["@vis.gl/react-google-maps" :refer [APIProvider ControlPosition Map MapControl]]))

(defn- compute-review-status
  "Determines POI marker color based on observation status.
   Returns :red if any non-clear observation exists,
   :green if all tasks have clear observations,
   :pending (blue) otherwise."
  [review-id tasks observations]
  (let [review-obs (filter #(= (:review-id %) review-id) observations)
        non-clear (filter #(false? (:clear %)) review-obs)
        clear (filter #(true? (:clear %)) review-obs)]
    (cond
      (seq non-clear) :red
      (and (seq tasks)
           (every? (fn [task]
                     (some #(= (:task-id %) (:id task)) clear))
                   tasks)) :green
      :else :pending)))

(defn api-zones->gmaps-data
  [zones reviews observations]
  (let [zone-nodes (mapv (fn [{:keys [id name area subzones]}]
                           #:zone{:id id
                                  :name name
                                  :polygon area
                                  :subzones (or subzones #{})})
                         zones)
        poi-nodes (keep (fn [{:keys [id type zone-id wp wp-type tasks lat-lng]}]
                          (when lat-lng
                            #:poi{:id id
                                  :type (case type
                                          :focused :focused-revision
                                          :general :general-revision
                                          :focused-revision)
                                  :marker lat-lng
                                  :parent-zone-id zone-id
                                  :status (compute-review-status id tasks observations)
                                  :work-package {:wp wp
                                                 :wp-type wp-type
                                                 :tasks tasks}}))
                        reviews)]
    (into zone-nodes poi-nodes)))

(defn compute-viewport
  [zones]
  (let [points (mapcat :area zones)
        lngs (map first points)
        lats (map second points)]
    (when (seq points)
      {:center {:lat (/ (+ (apply min lats) (apply max lats)) 2)
                :lng (/ (+ (apply min lngs) (apply max lngs)) 2)}
       :zoom 19
       :min-zoom 17})))

(rf/reg-event-fx
 ::initialize-zones-map
 (fn [{:keys [db]} _]
   (let [service (:current-service db)
         zones (or (:zones service) [])
         reviews (or (:reviews service) [])
         observations (or (:observations service) [])
         gmaps-data (api-zones->gmaps-data zones reviews observations)
         viewport (compute-viewport zones)]
     (when (and (seq zones) viewport)
       {:db (-> db
                (assoc :default-map-viewport viewport)
                (assoc :gmaps/read-only? true))
        :fx [[:dispatch [::google-maps/initialize-zones gmaps-data]]]}))))

(defn- legends
  []
  [:div.zone-legends
   [:div.zone-legends-icon
    [google-maps/wp-marker {:wp :legend} "mr-4"]
    (t [(keyword "domain.choices.revision-type/focused-revision")])]
   [:div.zone-legends-icon
    [google-maps/wp-marker {:wp :legend} "mr-4 general-review-wp-icon"]
    (t [(keyword "domain.choices.revision-type/general-revision")])]])

(defn- zone-panel-card
  [{:zone/keys [name subzones pois]}]
  [:div.zone-card.zone-card--readonly
   [:div.zone-card__header
    [:span.zone-card__title name]
    (->> pois
         (filter (fn [poi] (= (:poi/type poi) :general-revision)))
         (map (fn [poi] (get-in poi [:poi/work-package :wp])))
         (distinct)
         (map (fn [wp] ^{:key (str wp)} [google-maps/wp-marker wp "ml-4 general-review-wp-icon"])))]
   (when (seq subzones)
     [:div.zone-card__children
      (for [sz subzones]
        ^{:key (str "zone-" (:zone/id sz))}
        [zone-panel-card sz])])])

(defn- zone-panel-readonly
  []
  (let [zones-tree (rf/subscribe [::google-maps/zones-tree])]
    (fn []
      (when (seq @zones-tree)
        [:div.zone-panel
         (doall
          (for [z @zones-tree]
            ^{:key (:zone/id z)}
            [zone-panel-card z]))]))))

(defn- zones-map
  []
  (let [default-map-viewport (rf/subscribe [::srv.project/default-map-viewport])]
    (fn []
      (let [{:keys [center zoom min-zoom]} @default-map-viewport]
        (when center
          [:div.si-zones-map
           [:> Map
            {:mapId "INSTALLER_SERVICE_INFORMATION_ZONE"
             :mapTypeId "satellite"
             :tilt 0
             :minZoom min-zoom
             :style {:height "50vh"}
             :defaultCenter center
             :defaultZoom zoom
             :disableDefaultUI true
             :zoomControl true
             :keyboardShortcuts false}
            [:f> google-maps/gmaps-bridge!]
            [:> MapControl {:position ControlPosition.TOP_LEFT}
             [zone-panel-readonly]]
            [:> MapControl {:position ControlPosition.BOTTOM_CENTER}
             [legends]]]])))))

(defn zones-map-wrapper
  []
  (let [config (rf/subscribe [::config/config-data])]
    (fn []
      (let [{:keys [google-maps-api-key]} @config]
        (when google-maps-api-key
          [:> APIProvider {:apiKey google-maps-api-key}
           [zones-map]])))))

(defn- type-name
  "Normalizes a value (keyword or string) to its name string for comparison."
  [v]
  (if (keyword? v) (name v) (str v)))

(defn- wp-card
  [{:keys [wp wp-type tasks]}]
  (let [wp-name (type-name wp)
        wp-type-name (type-name wp-type)
        wp-title (t [(keyword (str "domain.choices.wp/" wp-name))])]
    [:div.work-package-card
     [:div.work-package-card__header
      [:span.work-package-card__title
       [google-maps/wp-icon (keyword wp-name)]
       wp-title]
      #_[:div.zone-card__actions
         [:button.flat-btn {:type :button} [layout.icon/icon {:key :edit}]]
         [:button.flat-btn {:type :button} [layout.icon/icon {:key :delete}]]]]
     [:div.work-package-card__details
      (when (and wp-type-name (not= wp-type-name "nil"))
        [:span.work-package-card__subtitle
         (t [(keyword (str "domain.choices.wp-type/" wp-type-name))])])
      (for [{:keys [task-type comment]} tasks]
        ^{:key (str wp-name "-" (type-name task-type))}
        [:<>
         [:span.work-package-card__subtitle
          (t [(keyword (str "domain.choices.task-type/" (type-name task-type)))])]
         (when (and comment (seq comment))
           [:div.work-package-card__subtitle
            [layout.icon/icon {:key :message :class "mr-4"}]
            [:span.task-card__comments-text comment]])])]]))

(defn- revision-type-group
  [revision-type reviews]
  (when (seq reviews)
    [:div.work-package-group
     [:span.work-package-group__title
      (t [(keyword (str "domain.choices.revision-type/" (name revision-type)))])]
     [:div.work-package-group__list
      (for [review reviews]
        ^{:key (:id review)}
        [wp-card review])]]))

(defn- reviews-for-zone
  "Finds all reviews whose zone-id matches the given zone id,
   handling both keyword and string comparisons."
  [reviews zone-id]
  (let [zid (str zone-id)]
    (filter #(= (str (:zone-id %)) zid) reviews)))

(defn- split-by-type
  "Splits reviews into focused and general groups."
  [reviews]
  (let [focused (filter #(= (type-name (:type %)) "focused") reviews)
        general (filter #(= (type-name (:type %)) "general") reviews)]
    {:focused focused :general general}))

(defn- subzone-card
  [subzone all-reviews]
  (let [{:keys [id name]} subzone
        reviews (reviews-for-zone all-reviews id)
        {:keys [focused general]} (split-by-type reviews)]
    [:div.zone-card.zone-card--subzone
     [:div.zone-card__header
      [:span.zone-card__title.zone-card__title--subzone name]
      #_[:div.zone-card__actions
         [:button.flat-btn {:type :button} [layout.icon/icon {:key :edit}]]
         [:button.flat-btn {:type :button} [layout.icon/icon {:key :delete}]]]]
     [revision-type-group :focused-revision focused]
     [revision-type-group :general-revision general]]))

(defn- zone-card
  [zone all-zones all-reviews]
  (let [{:keys [id name subzones]} zone
        subzone-list (->> subzones
                          (map (fn [sz-id]
                                 (->> all-zones
                                      (filter #(= (str (:id %)) (str sz-id)))
                                      first)))
                          (remove nil?))
        own-reviews (reviews-for-zone all-reviews id)
        {:keys [focused general]} (split-by-type own-reviews)]
    [:div.zone-card
     [:div.zone-card__header
      [:span.zone-card__title name]
      #_[:div.zone-card__actions
         [:button.flat-btn {:type :button} [layout.icon/icon {:key :edit}]]
         [:button.flat-btn {:type :button} [layout.icon/icon {:key :delete}]]]]
     [:div.zone-card__children
      (when (seq subzone-list)
        (for [sz subzone-list]
          ^{:key (:id sz)}
          [subzone-card sz all-reviews]))
      [revision-type-group :focused-revision focused]
      [revision-type-group :general-revision general]]]))

(defn zones-tab
  [service]
  (let [zones (or (:zones @service) [])
        reviews (or (:reviews @service) [])
        all-subzone-ids (into #{} (map str (mapcat :subzones zones)))
        root-zones (remove #(contains? all-subzone-ids (str (:id %))) zones)]
    [:div.installer-service-information__tab-content
     [zones-map-wrapper]
     [entity-section
      {:title-transl-kw :common.zones-indicated}
      (if (seq zones)
        [:div.si-zones
         (for [zone root-zones]
           ^{:key (:id zone)}
           [zone-card zone zones reviews])]
        [:p (t [:common/no-data])])]]))
